<template>
  <div v-if="digitalizationProjectSettings" id="digitalization-project-details">
    <div class="title-panel">
      <h1>{{ digitalizationProjectSettings.title }}</h1>
      <vf-button
        :variant="buttonVariants.secondary"
        @click="() => goToProjectRoom()"
        >{{ $t('digitalizationProjectDetails.goToProjectRoom') }}</vf-button
      >
    </div>

    <PVTabs v-if="store.state.userRoles.length" v-model:value="activeTab" lazy>
      <PVTabList>
        <router-link
          :to="getTabLink(DigitalizationProjectDetailsTab.INFORMATION)"
        >
          <PVTab :value="DigitalizationProjectDetailsTab.INFORMATION">
            {{ $t('digitalizationProjectDetails.tabs.informations') }}
          </PVTab>
        </router-link>
        <router-link
          v-if="isUserRole(allowedRoles.digitalizationProjectSettings)"
          :to="getTabLink(DigitalizationProjectDetailsTab.SETTINGS)"
        >
          <PVTab :value="DigitalizationProjectDetailsTab.SETTINGS">
            {{ $t('digitalizationProjectDetails.tabs.settings') }}
          </PVTab>
        </router-link>
        <router-link
          v-if="isUserRole(allowedRoles.digitalizationProjectCoworkers)"
          :to="getTabLink(DigitalizationProjectDetailsTab.COWORKERS)"
        >
          <PVTab :value="DigitalizationProjectDetailsTab.COWORKERS">
            {{ $t('digitalizationProjectDetails.tabs.suppliers') }}
          </PVTab>
        </router-link>
        <router-link :to="getTabLink(DigitalizationProjectDetailsTab.UPLOADS)">
          <PVTab :value="DigitalizationProjectDetailsTab.UPLOADS">
            {{ $t('digitalizationProjectDetails.tabs.uploads') }}
          </PVTab>
        </router-link>
        <router-link
          :to="getTabLink(DigitalizationProjectDetailsTab.DOCUMENTS)"
        >
          <PVTab :value="DigitalizationProjectDetailsTab.DOCUMENTS">
            {{ $t('digitalizationProjectDetails.tabs.documents') }}
          </PVTab>
        </router-link>
        <router-link
          v-if="isUserRole(iblAllowedRoles)"
          :to="getTabLink(DigitalizationProjectDetailsTab.IBL)"
        >
          <PVTab :value="DigitalizationProjectDetailsTab.IBL">
            {{ $t('digitalizationProjectDetails.tabs.ibl') }}
          </PVTab>
        </router-link>
      </PVTabList>
      <PVTabPanels>
        <PVTabPanel :value="DigitalizationProjectDetailsTab.INFORMATION">
          <digitalization-project-informations
            :project-id="id"
            :project-data="digitalizationProjectSettings"
            @project-data-upated="getDigitalizationProjectSettings()"
          ></digitalization-project-informations>
        </PVTabPanel>
        <PVTabPanel :value="DigitalizationProjectDetailsTab.SETTINGS">
          <digitalization-project-settings
            :digitalization-project-id="id"
          ></digitalization-project-settings>
        </PVTabPanel>
        <PVTabPanel :value="DigitalizationProjectDetailsTab.COWORKERS">
          <digitalization-project-coworkers
            :digitalization-project-id="id"
          ></digitalization-project-coworkers>
        </PVTabPanel>
        <PVTabPanel :value="DigitalizationProjectDetailsTab.UPLOADS">
          <digitalization-uploads-overview
            :digitalization-project-settings="digitalizationProjectSettings"
          ></digitalization-uploads-overview>
        </PVTabPanel>
        <PVTabPanel :value="DigitalizationProjectDetailsTab.DOCUMENTS">
          <digitalization-documents-overview
            :digitalization-project-id="id"
          ></digitalization-documents-overview>
        </PVTabPanel>
        <PVTabPanel :value="DigitalizationProjectDetailsTab.IBL">
          <ibl-view
            :project-id="id"
            :project-type="ProjectType.DIGITALIZATION_PROJECT"
          />
        </PVTabPanel>
      </PVTabPanels>
    </PVTabs>
  </div>
</template>

<script lang="ts" setup>
  import { ref, onMounted } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { RouteLocationRaw } from 'vue-router';
  import { useStore } from 'vuex';
  import { useToast } from '@/composables/useToast';
  import { useRoles } from '@/composables/useRoles';
  import DigitalizationDocumentsOverview from '@/components/DigitalizationDocumentsOverview/DigitalizationDocumentsOverview.vue';
  import DigitalizationProjectCoworkers from '@/components/DigitalizationProjectCoworkers/DigitalizationProjectCoworkers.vue';
  import DigitalizationProjectInformations from '@/components/DigitalizationProjectInformations/DigitalizationProjectInformations.vue';
  import DigitalizationProjectSettings from '@/components/DigitalizationProjectSettings/DigitalizationProjectSettings.vue';
  import DigitalizationUploadsOverview from '@/components/DigitalizationUploadsOverview/DigitalizationUploadsOverview.vue';
  import IblView from '@/components/IblView/IblView.vue';
  import { IDigitalizationProjectDetailsDto, ProjectType, Role } from 'types';
  import { ProjectDataService, RedirectionService } from '@/services';
  import { DigitalizationProjectDetailsTab } from './DigitalizationProjectDetails.types';
  import { Route, routeParams } from '@/routes';
  import { useTabs } from '@/composables/useTabs';

  const props = defineProps<{
    id: string;
  }>();

  const { t } = useI18n();
  const store = useStore();
  const { createErrorToast } = useToast();
  const { isUserRole } = useRoles();

  /** Temporarily only admins are allowed to access the IBL view */
  const iblAllowedRoles = ref([Role.Admin]);
  const digitalizationProjectSettings = ref<
    IDigitalizationProjectDetailsDto | undefined
  >();
  const allowedRoles = ref({
    digitalizationProjectSettings: [Role.Admin, Role.DocuResponsible],
    digitalizationProjectCoworkers: [Role.Admin, Role.DocuResponsible],
  });

  const { activeTab } = useTabs(
    Route.DIGITALIZATION_PROJECT_DETAILS,
    DigitalizationProjectDetailsTab,
    DigitalizationProjectDetailsTab.INFORMATION,
  );
  function getTabLink(tab: DigitalizationProjectDetailsTab): RouteLocationRaw {
    return {
      name: Route.DIGITALIZATION_PROJECT_DETAILS,
      params: {
        [routeParams[Route.DIGITALIZATION_PROJECT_DETAILS].projectId]: props.id,
        [routeParams[Route.DIGITALIZATION_PROJECT_DETAILS].tab]: tab,
      },
    };
  }

  async function getDigitalizationProjectSettings() {
    try {
      const project = await ProjectDataService.getDigitalizationProjectData(
        props.id,
      );
      digitalizationProjectSettings.value = project;
    } catch (err) {
      createErrorToast(
        t('digitalizationProjectDetails.errors.errorLoadingProjectData'),
      );
    }
  }

  async function goToProjectRoom(): Promise<void> {
    if (!digitalizationProjectSettings.value) return;
    const url = await RedirectionService.getProjectRoomUrl(
      digitalizationProjectSettings.value?.title,
    );
    window.open(url, '_blank');
  }

  onMounted(getDigitalizationProjectSettings);
</script>

<style lang="scss" scoped>
  #digitalization-project-details {
    .title-panel {
      margin-bottom: 4.25rem;
      display: flex;
      justify-content: space-between;
      align-items: center;

      h1 {
        font-weight: 700;
        font-size: 50px;
        line-height: 36px;
      }
    }
  }
</style>
